import { getUser } from '@app/ducks';
import { isDefined } from '@app/utils/is';
import * as Sentry from '@sentry/react';
import React from 'react';
import { useSelector } from 'react-redux';

import { useSatisfactionSurvey } from '../client-api/useSatisfactionSurvey';

export type RedesignRating = 'sad' | 'meh' | 'happy';

export interface SubmitSurveyPayload {
  rating: RedesignRating;
  comment?: string;
  callback?: VoidFunction;
}

interface FeedbackSurveyHook {
  isSubmittingSurvey: boolean;
  submitSurvey: (payload: SubmitSurveyPayload) => void;
}

export const useFeedbackSurvey = (): FeedbackSurveyHook => {
  const user = useSelector(getUser);
  const { mutate: submitReview, isPending: isSubmittingSurvey } = useSatisfactionSurvey();

  const userRole = React.useMemo(() => {
    if (isDefined(user.email) && user.email.endsWith('@centra.com')) {
      return 'centra-user';
    }

    if (user.access === 'seller') {
      if (user.isAdmin) {
        return 'admin';
      }

      return 'agent';
    }

    return user.access;
  }, [user]);

  const submitSurvey = ({ rating, comment, callback }: SubmitSurveyPayload) => {
    submitReview(
      {
        comment,
        rating,
        userRole,
      },
      {
        onError: () => {
          Sentry.captureException('Error sending survey feedback', {
            extra: {
              comment,
              rating,
            },
          });
        },
        onSettled: callback,
      },
    );
  };

  return {
    isSubmittingSurvey,
    submitSurvey,
  };
};
